import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Provider } from 'react-redux'
import { NotificationContainer } from 'react-notifications'
import { setupStore } from '../store/store'
import { setMiscData } from '../store/miscSlice'
import { useGetRosterQuery } from '../store/rosterApi'
import RosterFilterBar from './RosterFilterBar'
import RosterActionBar from './RosterActionBar'
import RosterTable from './RosterTable'
import RosterPagination from './RosterPagination'
import ImportMasterRosterPanel from './ImportMasterRosterPanel'
import MemberFormModal from './MemberFormModal'

const t = (key) => (!window.I18n ? key : window.I18n.t(key))

const EMPTY_FILTERS = { filterLetters: '', column: '', sortOrder: '', page: 1 }

const GetStartedBanner = () => {
  const { data } = useGetRosterQuery(EMPTY_FILTERS)
  const hasNoMembers = !data || data.total_entries === 0

  if (!hasNoMembers) return null

  return (
    <div className='alert alert-warning product_warning stick_to_breadcrumb'>
      <strong>{t('customer_groups.groups.members.get_started_bold')}: </strong>
      {t('customer_groups.groups.members.get_started_body')}
    </div>
  )
}

const CustomerGroupMembersRoot = ({ fetchRosterUrl, masterRosterSearchUrl, addMemberUrl, createMemberManuallyUrl, updateMemberUrl, destroyMemberUrl, inviteMemberUrl, registerAllUrl, downloadRosterUrl, countOfEvents, eventsUrl, addGroupLeaderUrl, groupLeaderLimitReached, phoneFieldName, handicapProvider }) => {
  const [masterRosterImportModalOpen, setMasterRosterImportModalOpen] = useState(false)
  const [memberModalOpen, setMemberModalOpen] = useState(false)
  const [editMember, setEditMember] = useState(null)

  // Create the store once; recreating it wipes the RTK cache and blanks the table.
  const storeRef = useRef(null)
  if (!storeRef.current) {
    storeRef.current = setupStore()
    storeRef.current.dispatch(setMiscData({
      routes: { fetchRosterUrl, masterRosterSearchUrl, addMemberUrl, createMemberManuallyUrl, updateMemberUrl, destroyMemberUrl, inviteMemberUrl, registerAllUrl, downloadRosterUrl },
      phoneFieldName: phoneFieldName || null,
      handicapProvider: handicapProvider || null,
    }))
  }
  const store = storeRef.current

  const handleOpenEdit = (member) => {
    setEditMember(member)
    setMemberModalOpen(true)
  }

  const handleCloseMemberModal = () => {
    setMemberModalOpen(false)
    setEditMember(null)
  }

  return (
    <Provider store={store}>
      <div className='customer-group-members'>
        <GetStartedBanner />
        <div className='customer-group-members-filter-bar row add-margin-bottom-10'>
          <RosterFilterBar />
          <RosterActionBar
            onOpenMasterRosterImportModal={() => setMasterRosterImportModalOpen(true)}
            onOpenMemberModal={() => setMemberModalOpen(true)}
            countOfEvents={countOfEvents}
            eventsUrl={eventsUrl}
            addGroupLeaderUrl={addGroupLeaderUrl}
            groupLeaderLimitReached={groupLeaderLimitReached}
          />
        </div>
        <RosterTable onEditMember={handleOpenEdit} />
        <RosterPagination />
      </div>
      <ImportMasterRosterPanel
        isOpen={masterRosterImportModalOpen}
        onClose={() => setMasterRosterImportModalOpen(false)}
      />
      <MemberFormModal
        isOpen={memberModalOpen}
        onClose={handleCloseMemberModal}
        member={editMember}
        phoneFieldName={phoneFieldName}
        handicapProvider={handicapProvider}
      />
      <NotificationContainer />
    </Provider>
  )
}

CustomerGroupMembersRoot.propTypes = {
  fetchRosterUrl: PropTypes.string.isRequired,
  masterRosterSearchUrl: PropTypes.string.isRequired,
  addMemberUrl: PropTypes.string.isRequired,
  createMemberManuallyUrl: PropTypes.string.isRequired,
  updateMemberUrl: PropTypes.string.isRequired,
  destroyMemberUrl: PropTypes.string.isRequired,
  inviteMemberUrl: PropTypes.string.isRequired,
  registerAllUrl: PropTypes.string.isRequired,
  downloadRosterUrl: PropTypes.string.isRequired,
  countOfEvents: PropTypes.number.isRequired,
  eventsUrl: PropTypes.string.isRequired,
  addGroupLeaderUrl: PropTypes.string,
  groupLeaderLimitReached: PropTypes.bool,
  phoneFieldName: PropTypes.string,
  handicapProvider: PropTypes.string,
}

export default CustomerGroupMembersRoot
